import { forwardRef, useCallback, useRef } from 'react';
import cx from 'classnames';
import { useRefArray } from '../../../hooks/useRefArray';
import { isTruncated } from '../../../hooks/useTruncationTooltip';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../../locale/formatting/useFormatLocale';
import { postfixTestId } from '../../../util/testId';
import { Flow } from '../../Layouts/Flow';
import bundle from '../NovaComponents-Calendar-gui-icu.properties';
import cn from '../Calendar.css.json';
import '../Calendar.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const CalendarLegend = /*#__PURE__*/forwardRef(function CalendarLegend(props, ref) {
  const localize = useTextLocalizer();
  const locale = useFormatLocale();
  const {
    refs: textRefs,
    createSetRef: createTextRef
  } = useRefArray();
  const legendTextRef = useRef(null);
  const day = new Intl.DateTimeFormat(locale, {
    day: 'numeric'
  }).format(new Date(2022, 0, 4));
  const handleMouseOver = index => {
    toggleTooltip(index);
  };
  const toggleTooltip = useCallback(index => {
    legendTextRef.current = textRefs[index];
    const div = legendTextRef.current;
    if (!div) {
      return;
    }
    const content = div?.innerText;
    if (content?.length && isTruncated(div, content)) {
      div.setAttribute('title', content);
    } else {
      div.removeAttribute('title');
    }
  }, [textRefs]);
  const getItem = (index, className, text, id) => {
    return /*#__PURE__*/_jsxs("div", {
      className: cx(cn.legendItem),
      "aria-hidden": "true",
      children: [id === 'specialDay' ? /*#__PURE__*/_jsx("div", {
        "data-testid": postfixTestId(props.testId, `${id}-legendSquare`),
        className: cx(cn.legendSquare),
        "aria-hidden": "true",
        children: /*#__PURE__*/_jsx("div", {
          "data-testid": postfixTestId(props.testId, `${id}-legendSquare`),
          className: cx(className),
          "aria-hidden": "true",
          children: day
        })
      }) : /*#__PURE__*/_jsx("div", {
        "data-testid": postfixTestId(props.testId, `${id}-legendSquare`),
        className: cx(cn.legendSquare, className),
        "aria-hidden": "true",
        children: day
      }), /*#__PURE__*/_jsx("div", {
        ref: createTextRef(index),
        "data-testid": postfixTestId(props.testId, `${id}-legendText`),
        className: cx(cn.legendText),
        "aria-hidden": "true",
        onMouseEnter: () => handleMouseOver(index),
        children: text
      })]
    }, index);
  };
  const getLegendItem = legendItem => {
    switch (legendItem) {
      case 'Selected':
        return getItem(1, [cn.legendSelected], localize('selected.label'), 'selected');
      case 'NonWorkday':
        return getItem(2, [cn.legendNonWorkDay], props.nonWorkDayLegendText ?? localize('weekend.label'), 'weekend');
      case 'NotAvailable':
        return getItem(3, [cn.legendDisabled, cn.dayCellDisabled], localize('notAvailable.label'), 'notAvailable');
      case 'SpecialDay':
        return getItem(4, [cn.legendSpecialDay], props.specialDayLegendText ?? localize('specialDay.label'), 'specialDay');
      default:
        return getItem(0, [cn.legendToday], localize('today.label'), 'today');
    }
  };
  const getItemTemplate = legendArray => {
    return /*#__PURE__*/_jsxs(Flow, {
      spacing: "xxs",
      orientation: "vertical",
      ref: ref,
      children: [/*#__PURE__*/_jsx(Flow, {
        spacing: "m",
        orientation: "horizontal",
        children: legendArray.slice(0, 2).map(legendItem => getLegendItem(legendItem))
      }), legendArray.length > 2 ? /*#__PURE__*/_jsx(Flow, {
        spacing: "m",
        orientation: "horizontal",
        children: legendArray.slice(2, 4).map(legendItem => getLegendItem(legendItem))
      }) : null, legendArray.length === 5 ? /*#__PURE__*/_jsx(Flow, {
        spacing: "m",
        orientation: "horizontal",
        children: legendArray.slice(4).map(legendItem => getLegendItem(legendItem))
      }) : null]
    });
  };
  const getCustomLegendItems = () => {
    if (!props.legendItems) return;
    // Need to put legend items in correct order for display: Today, Selected, NonWorkday, NotAvailable, SpecialDay
    const sortedArray = [];
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    if (props.legendItems.find(element => element === 'Today')) sortedArray.push('Today');
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    if (props.legendItems.find(element => element === 'Selected')) sortedArray.push('Selected');
    if (props.legendItems.find(element => element === 'NonWorkday')) sortedArray.push('NonWorkday');
    if (props.legendItems.find(element => element === 'NotAvailable')) sortedArray.push('NotAvailable');
    if (props.legendItems.find(element => element === 'SpecialDay')) sortedArray.push('SpecialDay');
    return getItemTemplate(sortedArray);
  };
  const getAllLegendItems = () => {
    const allItemsArray = ['Today', 'Selected', 'NonWorkday', 'NotAvailable', 'SpecialDay'];
    return getItemTemplate(allItemsArray);
  };
  return /*#__PURE__*/_jsx("div", {
    className: cn.legend,
    children: props.legendItems === undefined || props.legendItems.length === 0 || props.legendItems.length === 5 ? getAllLegendItems() : props.legendItems.length > 0 ? getCustomLegendItems() : null
  });
});
//# sourceMappingURL=CalendarLegend.js.map